import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface MenuItemTextStyle {
  color: string;
  fontSize: number;
  fontWeight: number;
  fontStyle: 'normal' | 'italic';
  textTransform: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  letterSpacing: number;
  textShadow: string;
}

interface MenuItemTextEditorProps {
  restState: MenuItemTextStyle;
  hoverState: MenuItemTextStyle;
  onRestStateChange: (style: MenuItemTextStyle) => void;
  onHoverStateChange: (style: MenuItemTextStyle) => void;
  primaryColor: string;
  secondaryColor: string;
  isStaticMode?: boolean;
}

/**
 * MenuItemTextEditor - Comprehensive text styling editor for menu item titles
 */
const MenuItemTextEditor: React.FC<MenuItemTextEditorProps> = ({
  restState,
  hoverState,
  onRestStateChange,
  onHoverStateChange,
  primaryColor,
  secondaryColor,
  isStaticMode = false
}) => {
  const [activeTab, setActiveTab] = React.useState<'rest' | 'hover'>('rest');
  const colorPresets = [
    { name: 'Primary', color: primaryColor },
    { name: 'Secondary', color: secondaryColor },
    { name: 'White', color: '#ffffff' },
    { name: 'Black', color: '#2D2D2D' },
    { name: 'Sand', color: '#FFFBF2' },
    { name: 'Gold', color: '#c5b068' },
    { name: 'Blue', color: '#3f92ff' },
    { name: 'Green', color: '#3fc060' }
  ];

  const textTransformOptions = [
    { value: 'none', label: 'None' },
    { value: 'uppercase', label: 'UPPERCASE' },
    { value: 'lowercase', label: 'lowercase' },
    { value: 'capitalize', label: 'Capitalize' }
  ];

  const shadowPresets = [
    { name: 'None', shadow: 'none' },
    { name: 'Subtle', shadow: '0 1px 2px rgba(0, 0, 0, 0.1)' },
    { name: 'Medium', shadow: '0 2px 4px rgba(0, 0, 0, 0.2)' },
    { name: 'Strong', shadow: '0 4px 8px rgba(0, 0, 0, 0.3)' },
    { name: 'Glow Primary', shadow: `0 0 8px ${primaryColor}` },
    { name: 'Glow Secondary', shadow: `0 0 8px ${secondaryColor}` }
  ];

  const updateStyle = (
    state: 'rest' | 'hover',
    property: keyof MenuItemTextStyle,
    value: any
  ) => {
    const currentStyle = state === 'rest' ? restState : hoverState;
    const newStyle = { ...currentStyle, [property]: value };
    
    if (state === 'rest') {
      onRestStateChange(newStyle);
    } else {
      onHoverStateChange(newStyle);
    }
  };

  const copyRestToHover = () => {
    onHoverStateChange({ ...restState });
  };

  const copyHoverToRest = () => {
    onRestStateChange({ ...hoverState });
  };

  const resetToDefaults = (state: 'rest' | 'hover') => {
    const defaultStyle: MenuItemTextStyle = {
      color: '#ffffff',
      fontSize: 18,
      fontWeight: 600,
      fontStyle: 'normal',
      textTransform: 'none',
      letterSpacing: 0,
      textShadow: 'none'
    };

    if (state === 'rest') {
      onRestStateChange(defaultStyle);
    } else {
      onHoverStateChange(defaultStyle);
    }
  };

  const renderStyleEditor = (
    state: 'rest' | 'hover',
    style: MenuItemTextStyle
  ) => (
    <div className="space-y-4">
      {/* Color */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Text Colour</Label>
        <div className="flex gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start gap-2">
                <div 
                  className="w-4 h-4 rounded border border-gray-300"
                  style={{ backgroundColor: style.color }}
                />
                {style.color}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64">
              <div className="space-y-3">
                <Input
                  type="color"
                  value={style.color}
                  onChange={(e) => updateStyle(state, 'color', e.target.value)}
                  className="w-full h-10"
                />
                <Input
                  type="text"
                  value={style.color}
                  onChange={(e) => updateStyle(state, 'color', e.target.value)}
                  placeholder="#ffffff"
                  className="font-mono text-sm"
                />
                <div className="grid grid-cols-2 gap-1">
                  {colorPresets.map((preset) => (
                    <Button
                      key={preset.name}
                      variant="outline"
                      size="sm"
                      onClick={() => updateStyle(state, 'color', preset.color)}
                      className="justify-start gap-2 h-auto p-2"
                    >
                      <div 
                        className="w-3 h-3 rounded border border-gray-300"
                        style={{ backgroundColor: preset.color }}
                      />
                      <span className="text-xs">{preset.name}</span>
                    </Button>
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Font Size */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Font Size: {style.fontSize}px</Label>
        <Slider
          value={[style.fontSize]}
          onValueChange={([value]) => updateStyle(state, 'fontSize', value)}
          min={12}
          max={32}
          step={1}
          className="w-full"
        />
      </div>

      {/* Font Weight */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Font Weight: {style.fontWeight}</Label>
        <Slider
          value={[style.fontWeight]}
          onValueChange={([value]) => updateStyle(state, 'fontWeight', value)}
          min={300}
          max={900}
          step={100}
          className="w-full"
        />
      </div>

      {/* Font Style */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Font Style</Label>
        <Select
          value={style.fontStyle}
          onValueChange={(value: 'normal' | 'italic') => updateStyle(state, 'fontStyle', value)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="normal">Normal</SelectItem>
            <SelectItem value="italic">Italic</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Text Transform */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Text Transform</Label>
        <Select
          value={style.textTransform}
          onValueChange={(value: any) => updateStyle(state, 'textTransform', value)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {textTransformOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Letter Spacing */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Letter Spacing: {style.letterSpacing}px</Label>
        <Slider
          value={[style.letterSpacing]}
          onValueChange={([value]) => updateStyle(state, 'letterSpacing', value)}
          min={-2}
          max={4}
          step={0.1}
          className="w-full"
        />
      </div>

      {/* Text Shadow */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Text Shadow</Label>
        <div className="grid grid-cols-2 gap-1">
          {shadowPresets.map((preset) => (
            <Button
              key={preset.name}
              variant={style.textShadow === preset.shadow ? "default" : "outline"}
              size="sm"
              onClick={() => updateStyle(state, 'textShadow', preset.shadow)}
              className="text-xs"
            >
              {preset.name}
            </Button>
          ))}
        </div>
      </div>

      {/* Reset Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => resetToDefaults(state)}
        className="w-full"
      >
        Reset to Defaults
      </Button>
    </div>
  );

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Menu Item Text Styling</CardTitle>
        {!isStaticMode && (
          <div className="flex gap-2">
            {activeTab === 'rest' && (
              <Button
                variant="outline"
                size="sm"
                onClick={copyRestToHover}
                className="text-xs"
              >
                Copy Rest → Hover
              </Button>
            )}
            {activeTab === 'hover' && (
              <Button
                variant="outline"
                size="sm"
                onClick={copyHoverToRest}
                className="text-xs"
              >
                Copy Hover → Rest
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {isStaticMode ? (
          // Static mode - show only rest state editor without tabs
          <div className="mt-4">
            {renderStyleEditor('rest', restState)}
          </div>
        ) : (
          // Dynamic mode - show full tabs interface
          <Tabs defaultValue="rest" className="w-full" onValueChange={(value) => setActiveTab(value as 'rest' | 'hover')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="rest">Rest State</TabsTrigger>
              <TabsTrigger value="hover">Hover State</TabsTrigger>
            </TabsList>
            
            <TabsContent value="rest" className="mt-4">
              {renderStyleEditor('rest', restState)}
            </TabsContent>
            
            <TabsContent value="hover" className="mt-4">
              {renderStyleEditor('hover', hoverState)}
            </TabsContent>
          </Tabs>
        )}

        {/* Preview */}
        <div className="mt-6 p-4 bg-gray-100 rounded-md">
          <Label className="text-sm font-medium mb-2 block">Preview</Label>
          <div className="space-y-2">
            <div 
              className="transition-all duration-300"
              style={{
                color: restState.color,
                fontSize: `${restState.fontSize}px`,
                fontWeight: restState.fontWeight,
                fontStyle: restState.fontStyle,
                textTransform: restState.textTransform as any,
                letterSpacing: `${restState.letterSpacing}px`,
                textShadow: restState.textShadow
              }}
            >
              Sample Menu Item {isStaticMode ? '' : '(Rest)'}
            </div>
            {!isStaticMode && (
              <div 
                className="transition-all duration-300"
                style={{
                  color: hoverState.color,
                  fontSize: `${hoverState.fontSize}px`,
                  fontWeight: hoverState.fontWeight,
                  fontStyle: hoverState.fontStyle,
                  textTransform: hoverState.textTransform as any,
                  letterSpacing: `${hoverState.letterSpacing}px`,
                  textShadow: hoverState.textShadow
                }}
              >
                Sample Menu Item (Hover)
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MenuItemTextEditor;